import BaseController from "vamos/cockpit/base/controller/type/BaseController";
import ListService from "vamos/cockpit/base/services/ListService";
import P13nService from "vamos/cockpit/base/services/P13nService";
import VariantManagementService from "vamos/cockpit/base/services/VariantManagementService";
import ViewHandler from "vamos/cockpit/base/handler/ViewHandler";
import JSONModel from "sap/ui/model/json/JSONModel";
import Table from "sap/ui/table/Table";
import SideNavigation, { SideNavigation$ItemSelectEvent } from "sap/tnt/SideNavigation";
import { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import ODataPropertyBinding from "sap/ui/model/odata/v4/ODataPropertyBinding";
import { ODataListBinding$DataReceivedEvent } from "sap/ui/model/odata/v4/ODataListBinding";
import V4Context from "sap/ui/model/odata/v4/Context";
import NavContainer from "sap/m/NavContainer";
import NoProductionDateProblemDialogController from "vamos/cockpit/data/import/problem/controller/NoProductionDateProblemDialog.controller";
import ImportProblemDetailsDialogController from "vamos/cockpit/data/import/problem/controller/ImportProblemDetailsDialog.controller";
import { Button$PressEvent } from "sap/m/Button";

/**
 * @namespace vamos.cockpit.data.import.problem.controller
 * */
export default class ImportProblemListController extends BaseController {
    public listService: typeof ListService = ListService;
    public p13nService: typeof P13nService = P13nService;
    public variantManagement: typeof VariantManagementService = VariantManagementService;

    private _bInitialized = false;
    private readonly _sEntitySet = "Request";
    private _oDefaultVariantContext: any = {
        requestWithoutProductionDateTable: {
            conditions: { "*Number,CustomerName*": { $search: null } },
            sorters: {},
            filters: {},
            width: {},
            states: {},
        },
        incorrectOrderPositionTable: {
            conditions: {
                "*RequestNumber,ArticleNumber,VekaId,OuterLaminationName,InnerLaminationName,SealName,SealColorName,CarrierName,Description,AdditionalEquipmentName,GeometryName*":
                        { $search: null },
            },
            sorters: {},
            filters: {},
            width: {},
            states: {},
        },
    };

    private _sRouteName: string;
    private _oSideNavigation: SideNavigation;
    private _oRequestWithoutProdDateTable: Table;
    private _oIncorrectOrderPositionTable: Table;
    private _oImportProblemsCountPropertyBinding: ODataPropertyBinding;
    private _pVariantsLoaded: Promise<JSONModel>;
    private _bAfterViewBound: boolean;
    private bInitialized: boolean;

    onInit(): void {
        super.onInit();
        this._sRouteName = this._sRouteName || this.getRouter().getRouteInfoByHash(this.getHash())?.name;
        this.viewModel = new JSONModel({ CurrentPage: "requestWithoutProductionDate" });
        this.getView().setModel(this.viewModel, "view");
        this._oSideNavigation = this.getView().byId("sideNavigation") as SideNavigation;
        this._oRequestWithoutProdDateTable = this.getView().byId("requestWithoutProductionDateTable") as Table;
        this._oIncorrectOrderPositionTable = this.getView().byId("incorrectOrderPositionTable") as Table;
        this._oImportProblemsCountPropertyBinding = this.getModel().bindProperty("/ImportProblems/$count") as ODataPropertyBinding;
        this.getComponent()
                .getModel("app")
                .bindProperty("/Clients/CurrentClientName")
                .attachChange(() => {
                    this.callImportProblemCount();
                });
        this._pVariantsLoaded = VariantManagementService.initVariants.call(this);
        void this._pVariantsLoaded.then((oTablesModel) => {
            this.tableModel = oTablesModel;
            this.getView().setModel(this.tableModel, "tables");
            this.bindWithoutProductionDate();
            this.attachItemCloseToContainer();
        });
        if (!this.runInit) {
            this.getRouter().getRoute(this._sRouteName).attachPatternMatched((event: Route$PatternMatchedEvent) => this._onObjectMatched(event));
        }
    }

    public onAfterRendering(): void {
        this.getViewTablesAndRefresh();
    }

    public _onObjectMatched(event: Route$PatternMatchedEvent): void {
        this.callImportProblemCount();
        this._bAfterViewBound = true;
        this.handleTabHistoryFromListViews(event.getParameter("name"));
    }

    public onItemSelect(event: SideNavigation$ItemSelectEvent): void {
        const key = event.getSource().getSelectedKey();
        this.viewModel.setProperty("/CurrentPage", key);
        (this.byId("pageContainer") as NavContainer).to(this.getView().createId(key));
        if (!this.bInitialized && key === "incorrectOrderPosition") {
            this.bInitialized = true;
            this.bindIncorrectOrderPositions();
        }
    }

    public async onEditProductionDate(): Promise<void> {
        const selectedIndices = this._oRequestWithoutProdDateTable.getSelectedIndices();
        if (selectedIndices.length === 0) {
            return;
        }
        const selectedContexts = selectedIndices.map((index) => {
            return this._oRequestWithoutProdDateTable.getContextByIndex(index) as V4Context;
        });

        const view = await ViewHandler.getView("vamos.cockpit.data.import.problem.view.NoProductionDateProblemDialog", this.getComponent());
        const controller = view.getController() as NoProductionDateProblemDialogController;
        controller.openDialog(selectedContexts, () => {
            this._oRequestWithoutProdDateTable.getBinding("rows").refresh();
        });
    }

    public async onEditIncorrectPositions(event: Button$PressEvent): Promise<void> {
        const table = this.getTableByButtonEvent(event);
        const importProblemContexts = this.getSelectedTableContext(table) as V4Context[];
        const listView = this.getView();
        listView.setBusyIndicatorDelay(0);
        listView.setBusy(true);
        try {
            const view = await ViewHandler.getView("vamos.cockpit.data.import.problem.view.ImportProblemDetailsDialog", this.getComponent());
            const controller = view.getController() as ImportProblemDetailsDialogController;
            await controller.openDialog(importProblemContexts, () => {
                table.getBinding("rows").refresh();
            });
        } finally {
            listView.setBusy(false);
        }
    }

    public handleTableUpdatedEvent(event: ODataListBinding$DataReceivedEvent): void {
        this.listService.handleTableUpdatedEvent.call(this, event);
        const binding = event.getSource();
        if (
                binding.getPath() !== this._oRequestWithoutProdDateTable.getBindingPath("rows") ||
                !this._bAfterViewBound
        ) {
            return;
        }
        this._bAfterViewBound = false;
        // iMaxLength is an internal ODataListBinding field with no public type, mirroring the legacy check in ListService.handleTableUpdatedEvent
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
        if ((binding as any).iMaxLength !== 0) {
            return;
        }
        void this._oImportProblemsCountPropertyBinding.requestValue().then((count: number) => {
            if (count > 0) {
                this._oSideNavigation.setSelectedKey("incorrectOrderPosition");
                this._oSideNavigation.fireItemSelect();
            }
        });
    }

    private bindWithoutProductionDate(): void {
        this.listService.bindTableByEntitySet.call(
                this,
                "Request",
                this._oRequestWithoutProdDateTable,
                undefined,
                undefined,
                { $filter: "Canceled eq false and ProductionDate eq null" },
        );
    }

    private bindIncorrectOrderPositions(): void {
        this.listService.bindTableByEntitySet.call(this, "ImportProblem", this._oIncorrectOrderPositionTable);
    }

    private callImportProblemCount(): void {
        //after opening the orderpositiontab, data received function should handle it
        const binding = this._oIncorrectOrderPositionTable.getBinding("rows");
        if (binding) {
            return;
        }
        void this._oImportProblemsCountPropertyBinding.requestValue().then((count: number) => {
            this.viewModel.setProperty("/ImportProblemsCount", count);
        });
    }
}
